import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { CentrexSdaModel } from '../models/centrex-sda.model';
import { ApiService } from './api.service';
import { ServiceBase } from './service-base';

@Injectable({
  providedIn: 'root',
})
export class CentrexSdasService extends ServiceBase {
  private sdasCache = new Map<string, Observable<CentrexSdaModel[]>>();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {
    super();
  }

  private createHeaders(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const token = this.apiService.getAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  private handleError(error: any): Observable<never> {
    console.error('Erreur CentrexSdasService', error);
    return throwError(() => error);
  }

  getSdas(
    organizationId: string,
    platform: string,
  ): Observable<CentrexSdaModel[]> {
    const cacheKey = `${organizationId}_${platform}`;

    // retourne le cache si présent
    if (this.sdasCache.has(cacheKey)) {
      return this.sdasCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/api/centrex/sdas`;

    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    const request$ = this.http
      .get<CentrexSdaModel[]>(url, {
        headers,
      })
      .pipe(
        shareReplay(1),

        catchError((error) => {
          this.sdasCache.delete(cacheKey);
          return this.handleError(error);
        }),
      );

    this.sdasCache.set(cacheKey, request$);

    return request$;
  }

  getSdaById(
    organizationId: string,
    platform: string,
    sdaId: string,
  ): Observable<CentrexSdaModel> {
    const url = `${this.apiUrl}/api/centrex/sdas/${sdaId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .get<CentrexSdaModel>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  createSda(
    organizationId: string,
    platform: string,
    sda: CentrexSdaModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/sdas`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.post<boolean>(url, sda, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.sdasCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  updateSda(
    organizationId: string,
    platform: string,
    sdaId: string,
    sda: CentrexSdaModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/sdas/${sdaId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.patch<boolean>(url, sda, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.sdasCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  deleteSda(
    organizationId: string,
    platform: string,
    sdaId: string,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/sdas/${sdaId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.delete<boolean>(url, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.sdasCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  clearCache(): void {
    this.sdasCache.clear();
  }
}
